/**
 * Payment Modal Component
 * Modal for managing weekly payment records
 */

import React, { useState, useEffect } from 'react';
import {
    XMarkIcon,
    CurrencyDollarIcon,
    CalendarIcon,
    CheckCircleIcon,
    ClockIcon,
    XCircleIcon
} from '@heroicons/react/24/outline';
import api from '../utils/api';
import ConfirmDeleteDialog from './ConfirmDeleteDialog';

import Select, { components } from 'react-select';
import ReactCountryFlag from 'react-country-flag';
import { getAllISOCodes } from 'iso-country-currency';

// Generate currency options from ISO data
const CURRENCY_OPTIONS = getAllISOCodes()
    .map(item => ({
        value: item.currency,
        label: `${item.countryName} - ${item.currency} (${item.symbol})`,
        code: item.iso, // Country code for flag
        currency: item.currency,
        symbol: item.symbol,
        countryName: item.countryName
    }))
    .sort((a, b) => a.countryName.localeCompare(b.countryName));

// Custom option component with flag
const CurrencyOption = (props) => {
    return (
        <components.Option {...props}>
            <div className="flex items-center">
                <ReactCountryFlag
                    countryCode={props.data.code}
                    svg
                    style={{
                        width: '1.5em',
                        height: '1.5em',
                        marginRight: '8px'
                    }}
                />
                {props.data.label}
            </div>
        </components.Option>
    );
};

// Custom single value component with flag
const CurrencySingleValue = (props) => {
    return (
        <components.SingleValue {...props}>
            <div className="flex items-center">
                <ReactCountryFlag
                    countryCode={props.data.code}
                    svg
                    style={{
                        width: '1.5em',
                        height: '1.5em',
                        marginRight: '8px'
                    }}
                />
                {props.data.label}
            </div>
        </components.SingleValue>
    );
};

// Custom styles for react-select to match Tailwind inputs
const customStyles = {
    control: (base, state) => ({
        ...base,
        padding: '0.3rem',
        borderRadius: '0.5rem',
        borderColor: state.isFocused ? '#6366f1' : '#d1d5db',
        boxShadow: state.isFocused ? '0 0 0 2px rgba(99, 102, 241, 0.5)' : 'none',
        '&:hover': {
            borderColor: '#9ca3af'
        }
    }),
    option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected ? '#e0e7ff' : state.isFocused ? '#f3f4f6' : 'white',
        color: '#1f2937',
        cursor: 'pointer'
    })
};

const PaymentModal = ({ isOpen, onClose, weekData, userId, onSave }) => {
    const [formData, setFormData] = useState({
        ratePerContact: '',
        extras: '0',
        currency: 'USD',
        paymentStatus: 'Pending',
        notes: ''
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    // Load existing payment data if available
    useEffect(() => {
        if (weekData?.payment) {
            setFormData({
                ratePerContact: weekData.payment.ratePerContact || '',
                extras: weekData.payment.extras || '0',
                currency: weekData.payment.currency || 'USD',
                paymentStatus: weekData.payment.paymentStatus || 'Pending',
                notes: weekData.payment.notes || ''
            });
        } else {
            // Reset form for new payment
            setFormData({
                ratePerContact: '',
                extras: '0',
                currency: 'USD',
                paymentStatus: 'Pending',
                notes: ''
            });
        }
    }, [weekData]);

    // Calculate subtotal and total
    const subtotal = (parseFloat(formData.ratePerContact) || 0) * (weekData?.count || 0);
    const total = subtotal + (parseFloat(formData.extras) || 0);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const paymentData = {
                userId,
                week: weekData.week,
                weekStart: weekData.weekStart,
                weekEnd: weekData.weekEnd,
                totalContacts: weekData.count,
                ratePerContact: parseFloat(formData.ratePerContact) || 0,
                extras: parseFloat(formData.extras) || 0,
                currency: formData.currency,
                paymentStatus: formData.paymentStatus,
                notes: formData.notes
            };

            const response = await api.post('/weekly-payments', paymentData);

            if (response.data.success) {
                onSave(response.data.data);
                onClose();
            }
        } catch (err) {
            console.error('Save payment error:', err);
            setError(err.response?.data?.message || 'Failed to save payment');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!weekData?.payment?.id) return;
        setShowDeleteConfirm(true);
    };

    const confirmDelete = async () => {
        setLoading(true);
        setError(null);
        setShowDeleteConfirm(false);

        try {
            const response = await api.delete(`/weekly-payments/${weekData.payment.id}`);

            if (response.data.success) {
                // Call onSave with null to indicate deletion
                onSave(null);
                onClose();
            }
        } catch (err) {
            console.error('Delete payment error:', err);
            setError(err.response?.data?.message || 'Failed to delete payment');
        } finally {
            setLoading(false);
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'Paid':
                return <CheckCircleIcon className="h-5 w-5 text-green-600" />;
            case 'Pending':
                return <ClockIcon className="h-5 w-5 text-yellow-600" />;
            case 'Cancelled':
                return <XCircleIcon className="h-5 w-5 text-red-600" />;
            default:
                return null;
        }
    };

    const getCurrencySymbol = (code) => {
        // Find the first option that matches the currency code
        return CURRENCY_OPTIONS.find(c => c.value === code)?.symbol || code;
    };

    if (!isOpen || !weekData) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                {/* Background overlay */}
                <div
                    className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
                    onClick={onClose}
                />

                {/* Modal panel */}
                <div className="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
                    {/* Header */}
                    <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-5">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center">
                                <div className="bg-white bg-opacity-20 rounded-lg p-2 mr-3">
                                    <CurrencyDollarIcon className="h-6 w-6 text-white" />
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold text-white">
                                        Payment for Week {weekData.week}
                                    </h3>
                                    <p className="text-sm text-blue-100 mt-0.5 flex items-center">
                                        <CalendarIcon className="h-4 w-4 mr-1" />
                                        {new Date(weekData.weekStart).toLocaleDateString('en-US', {
                                            month: 'short',
                                            day: 'numeric',
                                            year: 'numeric'
                                        })}
                                        {' - '}
                                        {new Date(weekData.weekEnd).toLocaleDateString('en-US', {
                                            month: 'short',
                                            day: 'numeric',
                                            year: 'numeric'
                                        })}
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={onClose}
                                className="text-white hover:text-gray-200 transition-colors"
                            >
                                <XMarkIcon className="h-6 w-6" />
                            </button>
                        </div>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="px-6 py-6">
                        {error && (
                            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                                <p className="text-sm text-red-600">{error}</p>
                            </div>
                        )}

                        {/* Total Contacts (Read-only) */}
                        <div className="mb-5">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Total Contacts
                            </label>
                            <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
                                <p className="text-2xl font-bold text-blue-600">{weekData.count}</p>
                                <p className="text-xs text-gray-500 mt-1">Contacts added this week</p>
                            </div>
                        </div>

                        {/* Rate per Contact */}
                        <div className="mb-5">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Rate per Contact *
                            </label>
                            <div className="relative">
                                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 font-medium">
                                    {getCurrencySymbol(formData.currency)}
                                </span>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={formData.ratePerContact}
                                    onChange={(e) => setFormData({ ...formData, ratePerContact: e.target.value })}
                                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg font-semibold"
                                    placeholder="0.00"
                                    required
                                />
                            </div>
                        </div>

                        {/* Subtotal (Auto-calculated) */}
                        <div className="mb-5 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3">
                            <div className="flex items-center justify-between">
                                <span className="text-sm font-semibold text-gray-700">Subtotal</span>
                                <span className="text-xl font-bold text-blue-600">
                                    {getCurrencySymbol(formData.currency)} {subtotal.toFixed(2)}
                                </span>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                                {formData.ratePerContact || 0} × {weekData.count} contacts
                            </p>
                        </div>

                        {/* Extras/Bonus */}
                        <div className="mb-5">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Extras / Bonus (Optional)
                            </label>
                            <div className="relative">
                                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 font-medium">
                                    {getCurrencySymbol(formData.currency)}
                                </span>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={formData.extras}
                                    onChange={(e) => setFormData({ ...formData, extras: e.target.value })}
                                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="0.00"
                                />
                            </div>
                            <p className="text-xs text-gray-500 mt-1">Additional payment or adjustment</p>
                        </div>

                        {/* Total (Auto-calculated) */}
                        <div className="mb-5 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300 rounded-xl px-5 py-4">
                            <div className="flex items-center justify-between">
                                <span className="text-base font-bold text-gray-800">TOTAL</span>
                                <span className="text-3xl font-bold text-green-600">
                                    {getCurrencySymbol(formData.currency)} {total.toFixed(2)}
                                </span>
                            </div>
                        </div>

                        {/* Currency */}
                        <div className="mb-5">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Currency
                            </label>
                            <Select
                                value={CURRENCY_OPTIONS.find(option => option.value === formData.currency) || null}
                                onChange={(option) => {
                                    if (option) {
                                        setFormData({ ...formData, currency: option.value });
                                    }
                                }}
                                options={CURRENCY_OPTIONS}
                                components={{
                                    Option: CurrencyOption,
                                    SingleValue: CurrencySingleValue
                                }}
                                styles={customStyles}
                                formatOptionLabel={(option) => (
                                    <div className="flex items-center">
                                        <ReactCountryFlag
                                            countryCode={option.code}
                                            svg
                                            style={{
                                                width: '1.2em',
                                                height: '1.2em',
                                                marginRight: '8px'
                                            }}
                                        />
                                        <span>{option.countryName} - {option.currency} ({option.symbol})</span>
                                    </div>
                                )}
                                placeholder="Select currency..."
                                isSearchable
                            />
                        </div>

                        {/* Payment Status */}
                        <div className="mb-5">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Payment Status
                            </label>
                            <div className="grid grid-cols-3 gap-3">
                                {['Pending', 'Paid', 'Cancelled'].map(status => (
                                    <button
                                        key={status}
                                        type="button"
                                        onClick={() => setFormData({ ...formData, paymentStatus: status })}
                                        className={`flex items-center justify-center px-4 py-3 rounded-lg border-2 transition-all ${formData.paymentStatus === status
                                            ? status === 'Paid'
                                                ? 'bg-green-50 border-green-500 text-green-700'
                                                : status === 'Pending'
                                                    ? 'bg-yellow-50 border-yellow-500 text-yellow-700'
                                                    : 'bg-red-50 border-red-500 text-red-700'
                                            : 'bg-white border-gray-300 text-gray-600 hover:border-gray-400'
                                            }`}
                                    >
                                        {getStatusIcon(status)}
                                        <span className="ml-2 font-semibold text-sm">{status}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Notes */}
                        <div className="mb-6">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Notes (Optional)
                            </label>
                            <textarea
                                value={formData.notes}
                                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                rows={3}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
                                placeholder="Add any additional notes..."
                            />
                        </div>

                        {/* Actions */}
                        <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                            {/* Delete button - only show if payment exists */}
                            <div>
                                {weekData?.payment && (
                                    <button
                                        type="button"
                                        onClick={handleDelete}
                                        disabled={loading}
                                        className="px-4 py-2.5 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition-colors shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                                    >
                                        <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                        {loading ? 'Deleting...' : 'Delete Payment'}
                                    </button>
                                )}
                            </div>

                            {/* Cancel and Save buttons */}
                            <div className="flex items-center gap-3">
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 font-semibold hover:bg-gray-50 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg font-semibold hover:from-blue-700 hover:to-blue-800 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {loading ? 'Saving...' : 'Save Payment'}
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>

            {/* Delete Confirmation Dialog */}
            <ConfirmDeleteDialog
                isOpen={showDeleteConfirm}
                onClose={() => setShowDeleteConfirm(false)}
                onConfirm={confirmDelete}
                title="Delete Payment Record"
                message={
                    weekData?.payment ? (
                        <div>
                            <p className="mb-3">Are you sure you want to delete this payment record?</p>
                            <div className="bg-gray-50 rounded-lg p-3 text-sm">
                                <p><strong>Week:</strong> {weekData.week}</p>
                                <p><strong>Amount:</strong> {weekData.payment.currency} {parseFloat(weekData.payment.total).toFixed(2)}</p>
                                <p><strong>Status:</strong> {weekData.payment.paymentStatus}</p>
                            </div>
                            <p className="mt-3 text-red-600 font-medium">This action cannot be undone.</p>
                        </div>
                    ) : "Are you sure you want to delete this payment record?"
                }
                confirmText="Delete Payment"
                loading={loading}
            />
        </div>
    );
};

export default PaymentModal;
